import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { performLogout } from '../slices/authSlice';
import authFetch from '../utils/authFetch';
import { useSelector } from 'react-redux';

const API_BASE = 'https://cvanalyzerback.osc-fr1.scalingo.io/api';

const ModalDeleteAccount = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const CONFIRM_WORD = 'SUPPRIMER';
  const isConfirmValid = confirmChecked && confirmText.trim().toUpperCase() === CONFIRM_WORD;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isConfirmValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authFetch(`${API_BASE}/users/me`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `Erreur ${response.status}`);
      }

      dispatch(performLogout());
      onClose();
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err.message ||
          'Impossible de supprimer le compte. Réessayez ou contactez le support.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Supprimer mon compte
                </h2>
                <p className="text-sm text-gray-500">Droit à l&apos;effacement (RGPD)</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <XMarkIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Action irréversible</p>
                <p>
                  La suppression de votre compte entraînera l&apos;effacement définitif de
                  vos données personnelles, de votre historique d&apos;analyses et de
                  tout contenu associé. Cette action ne peut pas être annulée.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <ShieldCheckIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Conformité RGPD</p>
                <p>
                  Vous disposez d&apos;un droit à l&apos;effacement (article 17 du RGPD).
                  En demandant la suppression de votre compte, nous procédons à
                  l&apos;effacement de vos données dans les délais prévus par la loi.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                Je comprends que cette action est définitive et irréversible, et que
                toutes mes données seront supprimées.
              </span>
            </label>

            <div>
              <label htmlFor="confirm-delete" className="block text-sm font-medium text-gray-700 mb-1">
                Pour confirmer, tapez <strong>{CONFIRM_WORD}</strong> ci-dessous :
              </label>
              <input
                id="confirm-delete"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!isConfirmValid || isSubmitting}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Suppression…
                  </span>
                ) : (
                  'Supprimer définitivement mon compte'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModalDeleteAccount;
